import {
  Box,
  Stack,
  Tab,
  Tabs,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { ReactNode, SyntheticEvent } from "react";
import { FeatureFlag, Permission } from "src/store/api/generatedApi";

export interface TabSpec<T extends string> {
  title: string;
  key: T;
  content: ReactNode;
  featureFlag?: FeatureFlag;
  permissionsToCheck?: Permission[];
  ignoreCatchall?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
}

export const TitledTabs = <T extends string>({
  tabSpecs,
  rightComponent,
  selectedTabKey,
  onChange,
  tabsSx,
  containerSx,
}: {
  tabSpecs: TabSpec<T>[];
  rightComponent?: ReactNode;
  selectedTabKey: T | undefined;
  onChange: (key: T) => void;
  tabsSx?: SystemStyleObject<Theme>;
  containerSx?: SystemStyleObject<Theme>;
}) => {
  const handleChange = (_: SyntheticEvent, newTabKey: T) => {
    onChange(newTabKey);
  };

  return (
    <Stack sx={{ height: "100%" }}>
      <Stack
        direction="row"
        sx={[
          tabsSx ?? {},
          {
            alignItems: "center",
            borderBottom: "1px solid black",
          },
        ]}
      >
        <Tabs
          value={selectedTabKey}
          onChange={handleChange}
          slotProps={{
            indicator: {
              sx: { height: "4px" },
            },
          }}
        >
          {tabSpecs.map(({ title, key, disabled, disabledTooltip }) => {
            const tab = (
              <Tab
                key={key}
                value={key}
                disabled={disabled}
                label={
                  <Typography variant="button" data-testid={`tab-${key}`}>
                    {title}
                  </Typography>
                }
                sx={{
                  textTransform: "none",
                  borderRadius: 0,
                  p: 1,
                  mr: 1,
                  color: "action.active",
                  "&.Mui-selected": { color: "primary.main" },
                }}
              />
            );
            // MUI Tabs uses cloneElement on direct children — wrapping in Tooltip
            // breaks that for non-disabled tabs. Only disabled tabs need the
            // span intermediary (Tooltip can't attach to pointer-events:none elements).
            if (!disabled) {
              return tab;
            }
            return (
              <Tooltip key={key} title={disabledTooltip ?? ""}>
                <span style={{ display: "inline-block" }}>{tab}</span>
              </Tooltip>
            );
          })}
        </Tabs>
        {rightComponent ? (
          <Box sx={{ marginLeft: "auto" }}>{rightComponent}</Box>
        ) : null}
      </Stack>
      {/* This way the "outermost scrolling box" is the content here, so the tabs will be fixed */}
      <Stack
        sx={[
          ...(containerSx ? [containerSx] : []),
          { pb: 2, flex: 1, overflow: "auto" },
        ]}
      >
        {tabSpecs.find(({ key }) => key === selectedTabKey)?.content}
      </Stack>
    </Stack>
  );
};
